import { compareDesc } from "date-fns";
import { LENS_DESKTOP, LENS_MOBILE } from "Constants";
import {
  ConnectedDeviceFragment,
  DeviceUserInfoFragment,
  DeviceWithConnectionsFragment,
  Maybe,
  Scalars
} from "generated/graphql";

export type DeviceUserInfo = DeviceUserInfoFragment & {
  lensDesktopInfo: ConnectedDeviceFragment | null | undefined;
  lensMobileInfo: ConnectedDeviceFragment | null | undefined;
  connectedDevices: ConnectedDeviceFragment[] | null | undefined;
};

const getAppInfo = (
  hardwareModel: string,
  connections: DeviceUserInfoFragment["connections"] | undefined,
  tenantId?: Maybe<Scalars["String"]> | undefined,
  deviceId?: Maybe<Scalars["String"]> | undefined
): DeviceWithConnectionsFragment | null => {
  if (!connections) return null;

  const lensApp = connections
    // keep connections that have:
    // 1. hardware model that matches provided hardware model
    // 2. belong to the current tenantId if provided
    // 3. matched to the current deviceId if provided
    // finally, sort by date ascending to get the lastDetected app instance
    .filter(connection => connection.hardwareModel === hardwareModel)
    .filter(connection => {
      if (tenantId) {
        // if tenantId provided, connection must match
        return connection.tenant?.id === tenantId;
      }

      return true;
    })
    .filter(connection => {
      if (deviceId) {
        // if deviceId provided, connection must match
        return connection.id === deviceId;
      }

      return true;
    })
    // return the app instance that has been used most recently in the case there are multiple
    .sort((a, b) =>
      compareDesc(new Date(a.lastDetected), new Date(b.lastDetected))
    )[0];

  return lensApp;
};

export const getDesktopInfo = (
  connections: DeviceUserInfoFragment["connections"] | undefined,
  tenantId?: Maybe<Scalars["String"]> | undefined,
  deviceId?: Maybe<Scalars["String"]> | undefined
) => getAppInfo(LENS_DESKTOP, connections, tenantId, deviceId);

export const getMobileInfo = (
  connections: DeviceUserInfoFragment["connections"] | undefined,
  tenantId?: Maybe<Scalars["String"]> | undefined,
  deviceId?: Maybe<Scalars["String"]> | undefined
) => getAppInfo(LENS_MOBILE, connections, tenantId, deviceId);

export type deviceWithClientType = ConnectedDeviceFragment & {
  clients: Array<Scalars["String"]>;
};

export const getUserConnectedDevices = (
  connections: DeviceUserInfoFragment["connections"] | undefined | null,
  deviceFilter?: string[] | undefined
) => {
  if (!connections) return null;
  const connectedDevices = connections?.reduce<deviceWithClientType[]>(
    (acc, curr) => {
      if (
        !acc.some(
          el => el.id.toLocaleLowerCase() === curr.id.toLocaleLowerCase()
        ) &&
        curr.hardwareModel !== null &&
        curr.hardwareModel !== LENS_DESKTOP &&
        curr.hardwareModel !== LENS_MOBILE
      ) {
        const devWithCli: deviceWithClientType = { ...curr, clients: [] };
        if (curr.connections) {
          curr.connections // keep connections that have hardware model as Lens Desktop or Mobile, that matches with (optional) filter
            .filter(connection => {
              return (
                (connection.hardwareModel === LENS_DESKTOP ||
                  connection.hardwareModel === LENS_MOBILE) &&
                (!deviceFilter?.length ||
                  deviceFilter?.includes(connection.hardwareModel))
              );
            })
            // Determine the device is connected via which clients (Lens Mobile / Lens Desktop / Both)
            .forEach(connection => {
              if (
                connection.hardwareModel === LENS_DESKTOP &&
                !devWithCli.clients.includes(LENS_DESKTOP)
              )
                devWithCli.clients.push(LENS_DESKTOP);
              if (
                connection.hardwareModel === LENS_MOBILE &&
                !devWithCli.clients.includes(LENS_MOBILE)
              )
                devWithCli.clients.push(LENS_MOBILE);
            });
        }
        acc.push(devWithCli);
      }
      return acc;
    },
    []
  );

  // Filter devices by clients
  let filetredConnections = connectedDevices.filter(device => {
    return !deviceFilter?.length || device.clients?.length;
  });

  return filetredConnections;
};

export const getDeviceConnections = (
  connections: DeviceWithConnectionsFragment["connections"] | undefined | null
) => {
  if (!connections) return null;
  return connections.reduce<ConnectedDeviceFragment[]>((acc, curr) => {
    if (
      !acc.some(
        el => el.id.toLocaleLowerCase() === curr.id.toLocaleLowerCase()
      ) &&
      curr.hardwareModel !== null &&
      curr.hardwareModel !== LENS_DESKTOP &&
      curr.hardwareModel !== LENS_MOBILE
    ) {
      acc.push(curr);
    }
    return acc;
  }, []);
};
